import { UrlUtils } from '@streamamg/amg-common';
import { StreamPlayPagingData } from '../types/stream-play-api.type';
import { StreamPlayFilters } from '../types/stream-play-models.type';

export class StreamPlayUtils {
  /**
   * Uses the paging data from the API to check if more data is available.
   * @param pagingData Stream Play paging data.
   *
   * @see StreamPlayPagingData
   */
  public static isMoreDataAvailable(pagingData: StreamPlayPagingData): boolean {
    return !(pagingData.offset + pagingData.limit >= pagingData.total);
  }

  /**
   * Generates a URL to be used for Stream Play based on the parameters. This will grab the URL, extracts the
   * parameters and merge them with the parameters generated from the given object if given.
   * @param streamPlayUrl The URL
   * @param pagingData Stream Play paging data
   * @param streamPlayFilters Stream Play filters
   * @param pageSize The dataset size
   *
   * @see StreamPlayPagingData
   * @see StreamPlayFilters
   */
  public static generateStreamPlayUrl(
    streamPlayUrl: string,
    pagingData?: StreamPlayPagingData,
    streamPlayFilters?: StreamPlayFilters,
    pageSize?: number
  ): string {
    const newPagingData = pagingData
        ? {
          limit: pagingData.limit.toString(),
          offset: (pagingData.offset + pagingData.limit).toString()
        } : {
          offset: '0',
          ...(typeof pageSize === 'number' ? { limit: pageSize.toString() } : {})
        };

    const urlParamsObject = {
      ...StreamPlayUtils.generateAndMergeStreamPlayQueryParamsFilters(streamPlayFilters, streamPlayUrl),
      ...newPagingData
    };

    const urlWithoutParams = UrlUtils.removeQueryParameters(streamPlayUrl);
    const urlParams = UrlUtils.generateUrlParameters(urlParamsObject);

    return `${urlWithoutParams}?${urlParams}`;
  }

  /**
   * Extracts the existing query parameters from the given URL while generating the filters from the given stream play
   * filters. At the end, the query parameters are merged while the latter takes precedence.
   * @param streamPlayFilters Stream Play filters
   * @param streamPlayUrl The URL
   * @param operator Generates OR or AND between filters
   *
   * @see StreamPlayFilters
   */
  public static generateAndMergeStreamPlayQueryParamsFilters(
    streamPlayFilters: StreamPlayFilters,
    streamPlayUrl: string,
    operator?: ('AND'|'OR')
  ): { [key: string]: string } {
    operator = operator || 'AND';
    const {
      q: querySearchParameter,
      ...existingUrlParameters
    } = UrlUtils.extractUrlParamsFromExistingUrl(streamPlayUrl);

    const streamPlayParameterObject = StreamPlayUtils.generateStreamPlayQueryParamsFilters(streamPlayFilters, operator);

    if (querySearchParameter) {
      // Merge q parameters
      if (streamPlayParameterObject['q']) {
        streamPlayParameterObject['q'] = streamPlayParameterObject['q'].concat(` ${operator} `).concat(querySearchParameter);
      } else {
        streamPlayParameterObject['q'] = querySearchParameter;
      }
    }

    return {
      ...existingUrlParameters,
      ...streamPlayParameterObject
    };
  }

  /**
   * Iterates through Stream Player Filters and converts any arrays into strings for query parameter generation.
   * @param streamPlayFilters Stream Play filters
   * @param operator Generates OR or AND between filters
   *
   * @see StreamPlayFilters
   */
  public static generateStreamPlayQueryParamsFilters(
      streamPlayFilters: StreamPlayFilters,
      operator?: ('AND'|'OR')): { [key: string]: string } {
    const streamPlayParameters: { [key: string]: string } = {};

    Object.keys(streamPlayFilters).forEach(key => {
      const value = streamPlayFilters[key];

      if (typeof value === 'string') {
        streamPlayParameters[key] = value;
      } else {
        streamPlayParameters[key] = value.join(` ${operator} `);
      }
    });

    return streamPlayParameters;
  }
}
